/**
 * Import SAP Page
 *
 * PURPOSE: Query SAP orders, select vehicles, and import them into the local database
 *
 * FEATURES:
 * - Query and browse SAP arrivals with filtering
 * - Multi-select rows for batch import
 * - Site assignment dialog before import
 * - Duplicate detection (prevent importing existing VINs)
 * - Batch import with progress indication
 * - Link imported cars to work instructions via item_code
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Filter, Loader2, Search, Download, Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { SiteSelector } from '@/components/SiteSelector';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { fetchArrivals, fetchItemCodes } from '@/services/sapProxyApi';
import { supabase } from '@/integrations/supabase/client';
import type { Arrival } from '@/types/arrivals';
import AppHeader from '@/components/AppHeader';

interface Line {
  id: string;
  name: string;
  display_name: string;
  short_display_name: string;
  factory_id: string;
  factory_name: string;
  area_id: string;
  area_name: string;
}

const ImportSAP = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLineId, setCurrentLineId, currentTenantId, allLines, currentFactoryId, currentAreaId } = useAuth();
  const { canManage, isReady: rolesReady } = useSiteRoles(currentLineId || undefined);

  // Data state
  const [arrivals, setArrivals] = useState<Arrival[]>([]);
  const [itemCodes, setItemCodes] = useState<string[]>([]);
  const [lines, setLines] = useState<Line[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [selectedItemCode, setSelectedItemCode] = useState<string>('PAKYCCPJ3 401');
  const [vinSearch, setVinSearch] = useState<string>('');

  // Selection state
  const [selectedVINs, setSelectedVINs] = useState<Set<string>>(new Set());
  const [existingVINs, setExistingVINs] = useState<Set<string>>(new Set());

  // Import dialog state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedLineId, setSelectedLineId] = useState<string>('');
  const [importing, setImporting] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  // Column visibility state - all SAP fields available
  const [columnVisibility, setColumnVisibility] = useState({
    docNum: true,
    docEntry: false,
    lineNum: false,
    vin: true,
    itemCode: true,
    kommNr: false,
    kennNr: true,
    kennNrF: false,
    artikelNr: false,
    datum: true,
    createDate: false,
    stockoutDatum: false,
    nakladkaDatum: false,
    status: true,
    zdroj: false,
    quantity: false,
  });

  /**
   * Check if user has permission to access this page
   */
  usePermissionGuard({
    enabled: rolesReady && !!currentLineId,
    allowed: canManage,
    message: t('importSAP.errors.accessDenied'),
  });

  /**
   * Load initial data on mount
   */
  useEffect(() => {
    loadItemCodes();
    const filteredLines = allLines.filter((line) => {
      if (currentFactoryId && line.factory_id !== currentFactoryId) return false;
      if (currentAreaId && line.area_id !== currentAreaId) return false;
      return true;
    });
    setLines(filteredLines);
    if (currentLineId) {
      setSelectedLineId(currentLineId);
    }
  }, [allLines, currentLineId, currentFactoryId, currentAreaId]);

  /**
   * Load arrivals when filters or pagination changes
   */
  useEffect(() => {
    loadArrivals();
  }, [currentPage, selectedItemCode]);

  /**
   * Fetch available item codes from API
   */
  const loadItemCodes = async () => {
    try {
      const codes = await fetchItemCodes();
      setItemCodes(codes);
    } catch (error) {
      console.error('Error loading item codes:', error);
      toast.error(t('importSAP.errors.loadItemCodes'));
    }
  };

  /**
   * Fetch available lines from database
   */
  /**
   * Fetch arrivals data from SAP API and check for existing VINs
   */
  const loadArrivals = async () => {
    setLoading(true);
    try {
      const response = await fetchArrivals({
        itemCode: selectedItemCode || undefined,
        page: currentPage,
        pageSize,
      });

      setArrivals(response.data);
      setTotalPages(response.pagination.totalPages);

      // Check which VINs already exist in the database
      const vins = response.data
        .map(a => a.U_VIN)
        .filter((vin): vin is string => Boolean(vin));

      if (vins.length > 0) {
        if (!currentTenantId) {
          setExistingVINs(new Set());
          return;
        }

        const { data: existingCars } = await ((supabase as any)
          .schema('mod_styling'))
          .from('cars')
          .select('vin')
          .in('vin', vins)
          .eq('tenant_id', currentTenantId);

        setExistingVINs(new Set(existingCars?.map(c => c.vin) || []));
      }
    } catch (error) {
      console.error('Error loading arrivals:', error);
      toast.error(t('importSAP.errors.loadArrivals'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle item code filter change
   */
  const handleItemCodeChange = (value: string) => {
    setSelectedItemCode(value);
    setCurrentPage(1);
    setSelectedVINs(new Set()); // Clear selection when filter changes
  };

  /**
   * Clear all filters
   */
  const handleClearFilters = () => {
    setSelectedItemCode('');
    setVinSearch('');
    setCurrentPage(1);
  };

  /**
   * Toggle selection of a single VIN
   */
  const toggleVINSelection = (vin: string, isExisting: boolean) => {
    if (isExisting) {
      toast.error(t('importSAP.errors.alreadyExists'));
      return;
    }

    const newSelection = new Set(selectedVINs);
    if (newSelection.has(vin)) {
      newSelection.delete(vin);
    } else {
      newSelection.add(vin);
    }
    setSelectedVINs(newSelection);
  };

  /**
   * Toggle select all on current page
   */
  const toggleSelectAll = () => {
    const availableVINs = arrivals
      .filter(a => a.U_VIN && !existingVINs.has(a.U_VIN))
      .map(a => a.U_VIN!);

    if (availableVINs.every(vin => selectedVINs.has(vin))) {
      // Deselect all on this page
      const newSelection = new Set(selectedVINs);
      availableVINs.forEach(vin => newSelection.delete(vin));
      setSelectedVINs(newSelection);
    } else {
      // Select all on this page
      const newSelection = new Set(selectedVINs);
      availableVINs.forEach(vin => newSelection.add(vin));
      setSelectedVINs(newSelection);
    }
  };

  /**
   * Open import dialog
   */
  const handleOpenImportDialog = () => {
    if (selectedVINs.size === 0) {
      toast.error(t('importSAP.errors.noSelection'));
      return;
    }
    setShowImportDialog(true);
  };

  /**
   * Import selected vehicles into the database
   */
  const handleImport = async () => {
    if (!selectedLineId || !currentTenantId) {
      toast.error(t('importSAP.errors.noSite'));
      return;
    }

    setImporting(true);

    try {
      const selectedArrivals = arrivals.filter(a => a.U_VIN && selectedVINs.has(a.U_VIN));

      let successCount = 0;
      let errorCount = 0;

      for (const arrival of selectedArrivals) {
        try {
          // Check for duplicate one more time
          const { data: existing } = await ((supabase as any)
            .schema('mod_styling'))
            .from('cars')
            .select('id')
            .eq('vin', arrival.U_VIN!)
            .eq('tenant_id', currentTenantId)
            .maybeSingle();

          if (existing) {
            console.warn(`Car ${arrival.U_VIN} already exists, skipping`);
            errorCount++;
            continue;
          }

          // Create car with SAP metadata
          const { error: carError } = await ((supabase as any)
            .schema('mod_styling'))
            .from('cars')
            .insert({
              vin: arrival.U_VIN!,
              tenant_id: currentTenantId,
              line_id: selectedLineId,
              sap_doc_num: arrival.DocNum,
              sap_doc_entry: arrival.DocEntry,
              item_code: arrival.ItemCode,
              sap_kennummer: arrival.U_KennNr,
              work_order_id: arrival.DocNum?.toString(),
              model: arrival.ItemCode, // Use item code as model
              status: 'pending',
              sap_date: arrival.U_Datum || null, // Important SAP date
            });

          if (carError) {
            console.error(`Error creating car ${arrival.U_VIN}:`, carError);
            errorCount++;
          } else {
            successCount++;
          }
        } catch (err) {
          console.error(`Error processing ${arrival.U_VIN}:`, err);
          errorCount++;
        }
      }

      // Show result
      if (successCount > 0) {
        toast.success(t('importSAP.success.imported', { count: successCount }));
      }
      if (errorCount > 0) {
        toast.error(t('importSAP.success.partialSuccess', {
          successCount,
          failCount: errorCount,
        }));
      }

      // Refresh data and clear selection
      setShowImportDialog(false);
      setSelectedVINs(new Set());
      loadArrivals();
    } catch (error) {
      console.error('Error during import:', error);
      toast.error(t('importSAP.errors.importFailed'));
    } finally {
      setImporting(false);
    }
  };

  const hasActiveFilters = selectedItemCode !== '' || vinSearch !== '';

  /**
   * Format date strings to localized format with hours and minutes (no seconds)
   */
  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('cs-CZ', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  // Column visibility helpers
  const toggleColumn = (column: keyof typeof columnVisibility) => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const visibleColumnsCount = Object.values(columnVisibility).filter(Boolean).length;
  const totalColumnsCount = Object.keys(columnVisibility).length;

  const columnLabels = {
    docNum: t('importSAP.tableHeaders.docNum'),
    docEntry: t('importSAP.tableHeaders.docEntry'),
    lineNum: t('importSAP.tableHeaders.lineNum'),
    vin: t('importSAP.tableHeaders.vin'),
    itemCode: t('importSAP.tableHeaders.itemCode'),
    kommNr: t('importSAP.tableHeaders.commissionNumber'),
    kennNr: t('importSAP.tableHeaders.kennNumber'),
    kennNrF: t('importSAP.tableHeaders.registrationNumber'),
    artikelNr: t('importSAP.tableHeaders.articleNumber'),
    datum: t('workOrders.table.date'),
    createDate: t('importSAP.tableHeaders.createDate'),
    stockoutDatum: t('importSAP.tableHeaders.stockoutDate'),
    nakladkaDatum: t('importSAP.tableHeaders.loadingDate'),
    status: t('workOrders.table.status'),
    zdroj: t('importSAP.tableHeaders.source'),
    quantity: t('importSAP.tableHeaders.quantity'),
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader
        backTo="/"
        backLabel={t('back')}
        title={t('importSAP.title')}
        subtitle={t('importSAP.subtitle')}
        rightContent={(
          <SiteSelector
            selectedSiteId={currentLineId || undefined}
            onSiteChange={setCurrentLineId}
            showWhenSingle
          />
        )}
        action={
          selectedVINs.size > 0 ? (
            <Button onClick={handleOpenImportDialog} size="lg" className="gap-2">
              <Download className="h-4 w-4" />
              {t('importSAP.importSelected', { count: selectedVINs.size })}
            </Button>
          ) : null
        }
      />

      <main className="p-4 md:p-6 lg:p-8">
        <div className="max-w-[1800px] mx-auto space-y-6">

        {loading && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-border/60 bg-muted/60 shadow-sm">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
            <div className="space-y-0.5">
              <p className="text-sm font-semibold text-foreground">
                {t('importSAP.loadingFromSAP')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('importSAP.fetchingData')}
              </p>
            </div>
          </div>
        )}

        {/* Filters Card */}
        <Card className="shadow-sm border-border/50">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Filter className="h-4 w-4 text-primary" />
              </div>
              <CardTitle className="text-lg font-semibold">
                {t('importSAP.filters')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* VIN Search */}
              <div className="space-y-2">
                <Label htmlFor="vin-search" className="text-sm font-medium">
                  {t('importSAP.vinSearch')}
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="vin-search"
                    placeholder={t('importSAP.vinSearchPlaceholder')}
                    value={vinSearch}
                    onChange={(e) => setVinSearch(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={!vinSearch}
                    className="shrink-0"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Item Code Filter */}
              <div className="space-y-2">
                <Label htmlFor="item-code-filter" className="text-sm font-medium">
                  {t('importSAP.itemCodeFilter')}
                </Label>
                <Select
                  value={selectedItemCode || 'ALL_ITEMS'}
                  onValueChange={(value) => {
                    if (value === 'ALL_ITEMS') {
                      handleItemCodeChange('');
                    } else {
                      handleItemCodeChange(value);
                    }
                  }}
                >
                  <SelectTrigger id="item-code-filter">
                    <SelectValue placeholder={t('importSAP.allItemCodes')}>
                      {selectedItemCode || t('importSAP.allItemCodes')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    <SelectItem value="ALL_ITEMS" className="py-3">
                      <span className="text-muted-foreground">{t('importSAP.allItemCodes')}</span>
                    </SelectItem>
                    {itemCodes.map((code) => (
                      <SelectItem key={code} value={code} className="py-3">
                        <span className="font-medium">{code}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters Button */}
              {hasActiveFilters && (
                <div className="flex items-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="text-xs text-muted-foreground hover:text-destructive"
                  >
                    {t('importSAP.clearFilters')}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Data Summary Bar */}
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">{t('importSAP.showing')}</span>
            <span className="font-semibold text-primary text-lg">
              {arrivals?.length || 0}
            </span>
            <span className="text-muted-foreground">{t('importSAP.results')}</span>
            {selectedVINs.size > 0 && (
              <>
                <span className="text-muted-foreground">•</span>
                <span className="font-semibold text-green-600">
                  {selectedVINs.size} {t('importSAP.selected')}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Table Card */}
        <Card className="shadow-sm border-border/50">
          <CardContent className="p-6">
            {/* Column Visibility Toggle */}
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-muted-foreground">
                {t('importSAP.columnsVisible', { count: visibleColumnsCount, total: totalColumnsCount })}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    {t('importSAP.columns')}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-[min(400px,80vh)] overflow-y-auto">
                  {(Object.keys(columnVisibility) as Array<keyof typeof columnVisibility>).map((column) => {
                    const isVisible = columnVisibility[column];
                    return (
                      <div
                        key={column}
                        className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                        onClick={() => toggleColumn(column)}
                      >
                        <div className="w-4 h-4 mr-2 flex items-center justify-center">
                          {isVisible && <Check className="h-4 w-4 text-primary" />}
                        </div>
                        <span>{columnLabels[column]}</span>
                      </div>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="rounded-md border bg-card shadow-sm overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-12">
                      <Checkbox
                        checked={
                          arrivals.length > 0 &&
                          arrivals
                            .filter(a => a.U_VIN && !existingVINs.has(a.U_VIN))
                            .every(a => selectedVINs.has(a.U_VIN!))
                        }
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    {columnVisibility.docNum && <TableHead>{t('importSAP.tableHeaders.docNum')}</TableHead>}
                    {columnVisibility.docEntry && <TableHead>{t('importSAP.tableHeaders.docEntry')}</TableHead>}
                    {columnVisibility.lineNum && <TableHead>{t('importSAP.tableHeaders.lineNum')}</TableHead>}
                    {columnVisibility.vin && <TableHead>{t('importSAP.tableHeaders.vin')}</TableHead>}
                    {columnVisibility.itemCode && <TableHead>{t('importSAP.tableHeaders.itemCode')}</TableHead>}
                    {columnVisibility.kommNr && <TableHead>{t('importSAP.tableHeaders.commissionNumber')}</TableHead>}
                    {columnVisibility.kennNr && <TableHead>{t('importSAP.tableHeaders.kennNumber')}</TableHead>}
                    {columnVisibility.kennNrF && <TableHead>{t('importSAP.tableHeaders.registrationNumber')}</TableHead>}
                    {columnVisibility.artikelNr && <TableHead>{t('importSAP.tableHeaders.articleNumber')}</TableHead>}
                    {columnVisibility.datum && <TableHead>{t('workOrders.table.date')}</TableHead>}
                    {columnVisibility.createDate && <TableHead>{t('importSAP.tableHeaders.createDate')}</TableHead>}
                    {columnVisibility.stockoutDatum && <TableHead>{t('importSAP.tableHeaders.stockoutDate')}</TableHead>}
                    {columnVisibility.nakladkaDatum && <TableHead>{t('importSAP.tableHeaders.loadingDate')}</TableHead>}
                    {columnVisibility.status && <TableHead>{t('workOrders.table.status')}</TableHead>}
                    {columnVisibility.zdroj && <TableHead>{t('importSAP.tableHeaders.source')}</TableHead>}
                    {columnVisibility.quantity && <TableHead>{t('importSAP.tableHeaders.quantity')}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumnsCount + 1} className="h-24 text-center">
                        <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                      </TableCell>
                    </TableRow>
                  ) : arrivals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumnsCount + 1} className="h-24 text-center text-muted-foreground">
                        {t('importSAP.noResults')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    arrivals
                      .filter(arrival => {
                        if (!vinSearch) return true;
                        return arrival.U_VIN?.toLowerCase().includes(vinSearch.toLowerCase());
                      })
                      .map((arrival) => {
                        const vin = arrival.U_VIN || '-';
                        const isExisting = existingVINs.has(vin);
                        const isSelected = selectedVINs.has(vin);

                        return (
                          <TableRow
                            key={`${arrival.DocEntry}-${vin}`}
                            className={`hover:bg-muted/50 transition-colors ${
                              isSelected ? 'bg-green-50' : ''
                            }`}
                          >
                            <TableCell>
                              {isExisting ? (
                                <Badge variant="outline" className="text-xs">
                                  {t('importSAP.exists')}
                                </Badge>
                              ) : (
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleVINSelection(vin, isExisting)}
                                />
                              )}
                            </TableCell>
                            {columnVisibility.docNum && (
                              <TableCell className="font-medium">
                                {arrival.DocNum || '-'}
                              </TableCell>
                            )}
                            {columnVisibility.docEntry && (
                              <TableCell className="text-sm">
                                {arrival.DocEntry || '-'}
                              </TableCell>
                            )}
                            {columnVisibility.lineNum && (
                              <TableCell className="text-sm">
                                {arrival.LineNum || '-'}
                              </TableCell>
                            )}
                            {columnVisibility.vin && (
                              <TableCell className="font-mono text-sm">
                                {vin}
                              </TableCell>
                            )}
                            {columnVisibility.itemCode && (
                              <TableCell className="text-sm">
                                {arrival.ItemCode || '-'}
                              </TableCell>
                            )}
                            {columnVisibility.kommNr && (
                              <TableCell className="text-sm">
                                {arrival.U_KommNr || '-'}
                              </TableCell>
                            )}
                            {columnVisibility.kennNr && (
                              <TableCell className="text-sm">
                                {arrival.U_KennNr || '-'}
                              </TableCell>
                            )}
                            {columnVisibility.kennNrF && (
                              <TableCell className="text-sm">
                                {arrival.U_KennNrF || '-'}
                              </TableCell>
                            )}
                            {columnVisibility.artikelNr && (
                              <TableCell className="text-sm">
                                {arrival.U_ArtikelNr || '-'}
                              </TableCell>
                            )}
                            {columnVisibility.datum && (
                              <TableCell className="text-sm">
                                {formatDate(arrival.U_Datum)}
                              </TableCell>
                            )}
                            {columnVisibility.createDate && (
                              <TableCell className="text-sm">
                                {formatDate(arrival.CreateDate)}
                              </TableCell>
                            )}
                            {columnVisibility.stockoutDatum && (
                              <TableCell className="text-sm">
                                {formatDate(arrival.Stockout_Datum)}
                              </TableCell>
                            )}
                            {columnVisibility.nakladkaDatum && (
                              <TableCell className="text-sm">
                                {formatDate(arrival.Nakladka_Datum)}
                              </TableCell>
                            )}
                            {columnVisibility.status && (
                              <TableCell>
                                {arrival.DocStatus === 'O' ? (
                                  <Badge variant="default">{t('importSAP.statusOpen')}</Badge>
                                ) : arrival.DocStatus === 'C' ? (
                                  <Badge variant="secondary">{t('importSAP.statusClosed')}</Badge>
                                ) : (
                                  <span className="text-sm">-</span>
                                )}
                              </TableCell>
                            )}
                            {columnVisibility.zdroj && (
                              <TableCell className="text-sm">
                                {arrival.U_Zdroj || '-'}
                              </TableCell>
                            )}
                            {columnVisibility.quantity && (
                              <TableCell className="text-sm">
                                {arrival.Quantity || '-'}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                {t('workOrders.table.page')} {currentPage} {t('workOrders.table.of')} {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t('importSAP.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  {t('importSAP.next')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </main>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('importSAP.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('importSAP.dialogDescription', { count: selectedVINs.size })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="line-select">{t('importSAP.assignToSite')}</Label>
              <Select value={selectedLineId} onValueChange={setSelectedLineId}>
                <SelectTrigger id="line-select" className="justify-between">
                  <span className="flex-1 text-left truncate">
                    {lines.find(line => line.id === selectedLineId)?.name || t('importSAP.selectSite')}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  {lines.map((line) => (
                    <SelectItem key={line.id} value={line.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{line.name}</span>
                        <span className="text-xs text-muted-foreground">{`${line.factory_name} / ${line.area_name}`}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedLineId && (
              <div className="rounded-lg bg-muted p-3 text-sm">
                <p className="font-medium mb-1">{t('importSAP.importSummary')}</p>
                <ul className="space-y-1 text-muted-foreground">
                  <li
                    dangerouslySetInnerHTML={{
                      __html: `• ${t('importSAP.summaryVehicles', { count: selectedVINs.size })}`,
                    }}
                  />
                  <li
                    dangerouslySetInnerHTML={{
                      __html: `• ${t('importSAP.summaryAssignedTo', {
                        siteName: lines.find(line => line.id === selectedLineId)?.name,
                      })}`,
                    }}
                  />
                  <li
                    dangerouslySetInnerHTML={{
                      __html: `• ${t('importSAP.summaryStatus')}`,
                    }}
                  />
                  <li
                    dangerouslySetInnerHTML={{
                      __html: `• ${t('importSAP.summaryAction')}`,
                    }}
                  />
                </ul>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowImportDialog(false)}
              disabled={importing}
            >
              {t('importSAP.cancel')}
            </Button>
            <Button
              onClick={handleImport}
              disabled={!selectedLineId || importing}
              className="gap-2"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('importSAP.importing')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {t('importSAP.importVehicles')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportSAP;
